import {
  uploadFileToS3,
  useCompleteUpload,
  useGenerateUploadUrl,
} from '@/queries/files';
import { formatBytes, getErrorMessage } from '@/utils';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloudUploadOutlined from '@ant-design/icons/CloudUploadOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Modal from 'antd/es/modal';
import Progress from 'antd/es/progress';
import Space from 'antd/es/space';
import Typography from 'antd/es/typography';
import Upload from 'antd/es/upload';
import type { RcFile, UploadFile } from 'antd/es/upload';
import React, { useState } from 'react';
import { styled, useTheme } from 'styled-components';

const { Text, Title } = Typography;
const { Dragger } = Upload;

const UploadContainer = styled.div`
  ${({ theme }) => `
  margin: ${theme.marginLG}px 0;
`}
`;

const FileInfoContainer = styled.div`
  ${({ theme }) => `
  background: ${theme.colorBgContainer};
  border: ${theme.lineWidth}px solid ${theme.colorBorder};
  border-radius: ${theme.borderRadiusLG}px;
  padding: ${theme.padding}px;
  margin: ${theme.margin}px 0;
`}
`;

const FileInfoRow = styled.div`
  ${({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.marginXS}px;

  &:last-child {
    margin-bottom: 0;
  }
`}
`;

const StatusContainer = styled.div`
  ${({ theme }) => `
  text-align: center;
  padding: ${theme.paddingXL}px;
`}
`;

interface UploadFileModalProps {
  open: boolean;
  onCancel: () => void;
  teamId: string;
  onSuccess?: () => void;
}

type UploadStatus = 'idle' | 'uploading' | 'processing' | 'success' | 'error';

export const UploadFileModal: React.FC<UploadFileModalProps> = ({
  open,
  onCancel,
  teamId,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const theme = useTheme();
  const generateUploadUrlMutation = useGenerateUploadUrl();
  const completeUploadMutation = useCompleteUpload();

  const validateFile = (file: RcFile): { isValid: boolean; errorMessage?: string } => {
    const hasValidExtension =
      file.name.includes('.') && (file.name.split('.').pop()?.length ?? 0) > 0;
    const hasValidMimeType = file.type !== '' || hasValidExtension;

    if (!hasValidMimeType) {
      return { isValid: false, errorMessage: 'Please select a valid file!' };
    }

    const isLt100M = file.size / 1024 / 1024 < 100;
    if (!isLt100M) {
      // file too big, do not allow upload
      return { isValid: false, errorMessage: 'File must be smaller than 100MB!' };
    }

    return { isValid: true };
  };

  const handleFileChange = (info: { fileList: UploadFile[] }) => {
    const latestFile = info.fileList.slice(-1);
    
    if (latestFile.length > 0 && latestFile[0]?.originFileObj) {
      const file = latestFile[0].originFileObj as RcFile;
      const validation = validateFile(file);
      
      if (!validation.isValid) {
        setFileList([]);
        return;
      }
    }
    
    setFileList(latestFile);
  };

  const beforeUpload = (file: RcFile) => {
    const validation = validateFile(file);
    
    if (!validation.isValid) {
      void message.error(validation.errorMessage!);
      return false;
    }

    return false;
  };

  const handleUpload = async () => {
    if (fileList.length === 0 || !fileList[0]?.originFileObj) {
      void message.error('Please select a file to upload');
      return;
    }

    const file = fileList[0].originFileObj as RcFile;
    await performUpload(file);
  };

  // Helper function to get MIME type from file extension
  const getMimeTypeFromExtension = (fileName: string): string | null => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const mimeMap: Record<string, string> = {
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'doc': 'application/msword',
      'pdf': 'application/pdf',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'xls': 'application/vnd.ms-excel',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'ppt': 'application/vnd.ms-powerpoint',
      'txt': 'text/plain',
      'csv': 'text/csv',
      'json': 'application/json',
      'xml': 'application/xml',
      'zip': 'application/zip',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'svg': 'image/svg+xml',
    };
    return ext ? mimeMap[ext] || null : null;
  };

  // Helper function to calculate SHA256 hash of a file
  const calculateFileHash = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const performUpload = async (file: RcFile) => {
    try {
      setUploadStatus('uploading');
      setUploadProgress(0);
      setErrorMessage(null);

      // Calculate file hash
      const fileHash = await calculateFileHash(file);

      // Step 1 - Generate upload URL
      const uploadUrlResponse = await generateUploadUrlMutation.mutateAsync({
        fileName: file.name,
        fileSize: file.size,
        fileHash,
        mimeType: file.type || getMimeTypeFromExtension(file.name) || 'application/octet-stream',
        teamId,
      });

      // Step 2 - Upload file to S3 using presigned URL
      await uploadFileToS3(uploadUrlResponse.uploadUrl, file, (progress) => {
        setUploadProgress(progress);
      });

      setUploadStatus('processing');

      // Step 3 - Mark upload as complete
      await completeUploadMutation.mutateAsync({
        fileId: uploadUrlResponse.file.fileId,
        fileSize: file.size,
        fileHash,
      });

      setUploadStatus('success');
      void message.success('File uploaded successfully!');

      // Clean up and close
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 1500);
    } catch (error) {
      setUploadStatus('error');
      const errorMsg = getErrorMessage(error, 'Failed to upload file');
      setErrorMessage(errorMsg);
      void message.error(errorMsg);
    }
  };

  const handleDeleteFile = () => {
    setFileList([]);
  };

  const handleClose = () => {
    setFileList([]);
    setUploadStatus('idle');
    setUploadProgress(0);
    setErrorMessage(null);
    onCancel();
  };

  const renderContent = () => {
    switch (uploadStatus) {
      case 'uploading':
        return (
          <StatusContainer>
            <LoadingOutlined
              style={{
                fontSize: `${theme.fontSizeHeading1 + 10}px`,
                color: theme.colorPrimary,
              }}
            />
            <Title level={4} style={{ marginTop: `${theme.margin}px` }}>
              Uploading your file...
            </Title>
            <Progress
              percent={uploadProgress}
              style={{
                maxWidth: `${theme.controlHeightLG * 10}px`,
                margin: '0 auto',
              }}
            />
            <br />
            <Text type="secondary" style={{ marginTop: `${theme.marginXS}px` }}>
              Please wait while we securely upload your file
            </Text>
          </StatusContainer>
        );

      case 'processing':
        return (
          <StatusContainer>
            <LoadingOutlined
              style={{
                fontSize: `${theme.fontSizeHeading1 + 10}px`,
                color: theme.colorPrimary,
              }}
              spin
            />
            <Title level={4} style={{ marginTop: `${theme.margin}px` }}>
              Processing your file...
            </Title>
            <Text type="secondary">
              Finalizing upload and preparing file for use
            </Text>
          </StatusContainer>
        );

      case 'success':
        return (
          <StatusContainer>
            <CheckCircleOutlined
              style={{
                fontSize: `${theme.fontSizeHeading1 + 10}px`,
                color: theme.colorSuccess,
              }}
            />
            <Title level={4} style={{ marginTop: `${theme.margin}px` }}>
              Upload successful!
            </Title>
            <Text type="secondary">
              Your file has been uploaded and is ready to use
            </Text>
          </StatusContainer>
        );

      case 'error':
        return (
          <StatusContainer>
            <Title level={4} style={{ color: theme.colorError }}>
              Upload failed
            </Title>
            <Text type="danger">{errorMessage}</Text>
            <Button
              type="primary"
              onClick={() => setUploadStatus('idle')}
              style={{ marginTop: `${theme.margin}px` }}
            >
              Try Again
            </Button>
          </StatusContainer>
        );

      default:
        return (
          <>
            <UploadContainer>
              <Dragger
                fileList={fileList}
                onChange={handleFileChange}
                beforeUpload={beforeUpload}
                maxCount={1}
                accept="*/*"
              >
                <p className="ant-upload-drag-icon">
                  <CloudUploadOutlined
                    style={{ fontSize: `${theme.fontSizeHeading1 + 10}px` }}
                  />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for any file type up to 100MB
                </p>
              </Dragger>
            </UploadContainer>

            {fileList.length > 0 && fileList[0] && (
              <FileInfoContainer>
                <FileInfoRow>
                  <Space>
                    <FileTextOutlined />
                    <Text strong>{fileList[0].name}</Text>
                  </Space>
                  <Space>
                    <Text type="secondary">
                      {formatBytes(fileList[0].size || 0)}
                    </Text>
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteFile}
                      danger
                    />
                  </Space>
                </FileInfoRow>
                <FileInfoRow>
                  <Text>File Type:</Text>
                  <Text type="secondary">{fileList[0].type || 'Unknown'}</Text>
                </FileInfoRow>
              </FileInfoContainer>
            )}
          </>
        );
    }
  };

  return (
    <Modal
      title="Upload File"
      open={open}
      onCancel={
        uploadStatus === 'idle' || uploadStatus === 'error'
          ? handleClose
          : undefined
      }
      footer={
        uploadStatus === 'idle'
          ? [
              <Button key="cancel" onClick={handleClose}>
                Cancel
              </Button>,
              <Button
                key="upload"
                type="primary"
                onClick={handleUpload}
                disabled={fileList.length === 0}
                icon={<UploadOutlined />}
              >
                Upload
              </Button>,
            ]
          : uploadStatus === 'error'
            ? [
                <Button key="close" onClick={handleClose}>
                  Close
                </Button>,
              ]
            : null
      }
      width={`${theme.controlHeightLG * 15}px`}
      closable={uploadStatus === 'idle' || uploadStatus === 'error'}
      maskClosable={false}
    >
      {renderContent()}
    </Modal>
  );
};

export default UploadFileModal;
